import { z } from 'zod';
import {
  TopicCompetitorReferenceModelOutput,
  TopicSkillHintModelOutput,
  type TopicCompetitorReference,
  type TopicSkillHint,
} from './analysis';
import { Platform } from './platform';
import type { SkillSummary } from './skill';

const GenerateDraftRequestFields = z.object({
  request_id: z.string().uuid().optional(),
  model_id: z.string().trim().min(1).max(128),
  platform: Platform,
  skill_id: z.string().trim().min(1).max(128).optional(),
  reference_note_id: z.string().trim().min(1).max(128).optional(),
  topic: z.string().trim().min(1).max(200),
  brief: z.string().trim().min(1).max(2000).optional(),
  source_saved_topic_id: z.string().trim().min(1).max(128).optional(),
});

function requireGenerationSource(
  value: z.infer<typeof GenerateDraftRequestFields>,
  ctx: z.RefinementCtx,
) {
  if (value.skill_id || value.reference_note_id) return;
  ctx.addIssue({
    code: 'custom',
    path: ['skill_id'],
    message: 'skill_id or reference_note_id is required',
  });
}

export const GenerateDraftRequest = GenerateDraftRequestFields.superRefine(requireGenerationSource);
export type GenerateDraftRequest = z.infer<typeof GenerateDraftRequest>;

export const GenerateDraftVariantCount = z.coerce.number().int().min(1).max(3);

export const GenerateDraftVariantsRequest = GenerateDraftRequestFields.extend({
  variant_count: GenerateDraftVariantCount.default(2),
}).superRefine(requireGenerationSource);
export type GenerateDraftVariantsRequest = z.infer<typeof GenerateDraftVariantsRequest>;

export type GeneratedDraft = {
  draft_id: string;
  version_id: string;
  aigc_record_id: string;
  platform: z.infer<typeof Platform>;
  title: string;
  body_md: string;
  status: 'draft';
  source_type: 'from_note' | 'from_topic';
  skill: SkillSummary | null;
  prompt_version_id: string | null;
  model_id: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  cost_cents: number;
  latency_ms: number;
  safety_status: DraftSafetyStatus;
  safety_meta: DraftSafetyNotice;
  created_at: string;
};

export type DraftSafetyStatus = 'safe' | 'flagged' | 'blocked';
export type DraftSafetyFlagSeverity = 'warning' | 'block';

export interface DraftSafetyFlag {
  code: string;
  severity: DraftSafetyFlagSeverity;
  message: string;
}

export interface DraftSafetySimilarity {
  score: number;
  matched_note_id: string;
  source_type: 'self_diag' | 'competitor' | 'topic' | 'public';
}

export interface DraftSafetyOriginality {
  candidate_limit: number;
  source_fields: string[];
  check_unavailable: boolean;
}

export interface DraftSafetyNotice {
  status: DraftSafetyStatus;
  copyable: boolean;
  flags: DraftSafetyFlag[];
  reason: string;
  similarity: DraftSafetySimilarity | null;
  originality: DraftSafetyOriginality;
}

export type GenerateDraftReadyResponse = {
  draft: GeneratedDraft;
  safety: DraftSafetyNotice;
};

export type GenerateDraftBlockedResponse = {
  draft: null;
  aigc_record_id: string;
  platform: z.infer<typeof Platform>;
  model_id: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  cost_cents: number;
  latency_ms: number;
  created_at: string;
  safety: DraftSafetyNotice;
};

export type GenerateDraftResponse = GenerateDraftReadyResponse;

export type DraftGenerationReadyItem = GenerateDraftReadyResponse & {
  status: 'ready';
  variant_index: number;
  topic_index?: number;
  generation_id: string;
  reservation_id: string;
};

export type DraftGenerationBlockedItem = GenerateDraftBlockedResponse & {
  status: 'blocked';
  variant_index: number;
  topic_index?: number;
  generation_id: string;
  reservation_id: string;
};

export type DraftGenerationFailureCode =
  | 'content_generation.prompt_unavailable'
  | 'content_generation.model_unavailable'
  | 'content_generation.model_failed'
  | 'content_generation.invalid_output'
  | 'content_generation.persistence_failed'
  | 'content_generation.internal_error';

export interface DraftGenerationFailedItem {
  status: 'failed';
  variant_index: number;
  topic_index?: number;
  generation_id: string;
  reservation_id: string;
  draft: null;
  safety: null;
  error: {
    code: DraftGenerationFailureCode;
  };
}

export type DraftGenerationItem = DraftGenerationReadyItem | DraftGenerationFailedItem;

export interface DraftGenerationSummary {
  total: number;
  ready: number;
  blocked: number;
  failed: number;
}

export interface GenerateDraftVariantsResponse {
  request_id: string;
  topic: string;
  brief: string | null;
  variant_count: number;
  items: DraftGenerationItem[];
  summary: DraftGenerationSummary;
}

export const ContentGenerationBatchTopic = z.object({
  topic: z.string().trim().min(1).max(200),
  brief: z.string().trim().max(2000).optional(),
  source_saved_topic_id: z.string().trim().min(1).max(128).optional(),
});
export type ContentGenerationBatchTopic = z.infer<typeof ContentGenerationBatchTopic>;

export const ContentGenerationBatchSubmitRequest = z
  .object({
    batch_id: z.string().uuid(),
    platform: Platform,
    mode: z.enum(['single', 'batch']),
    model_id: z.string().trim().min(1).max(128),
    skill_id: z.string().trim().min(1).max(128).optional(),
    reference_note_id: z.string().trim().min(1).max(128).optional(),
    reference_snapshot: z
      .object({
        title: z.string().max(500).nullable(),
        author: z.string().max(200).nullable(),
        summary: z.string().max(2000).nullable(),
        source_url: z.string().url().max(2000).nullable(),
      })
      .optional(),
    topics: z.array(ContentGenerationBatchTopic).min(1).max(5),
    variant_count: GenerateDraftVariantCount,
  })
  .superRefine((value, ctx) => {
    if (value.skill_id || value.reference_note_id) return;
    ctx.addIssue({
      code: 'custom',
      path: ['skill_id'],
      message: 'skill_id or reference_note_id is required',
    });
  });
export type ContentGenerationBatchSubmitRequest = z.infer<
  typeof ContentGenerationBatchSubmitRequest
>;

export const ContentGenerationBatchQuery = z.object({ platform: Platform });
export type ContentGenerationBatchQuery = z.infer<typeof ContentGenerationBatchQuery>;

export const ContentGenerationQueueMessage = z.object({
  batch_id: z.string().uuid(),
  generation_id: z.string().uuid(),
  tenant_id: z.string().min(1).max(128),
  owner_user_id: z.string().min(1).max(128),
});
export type ContentGenerationQueueMessage = z.infer<typeof ContentGenerationQueueMessage>;

export type ContentGenerationBatchStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'partially_succeeded'
  | 'failed';
export type ContentGenerationBatchItemStatus =
  | 'queued'
  | 'running'
  | 'ready'
  | 'blocked'
  | 'failed';

export interface ContentGenerationResourceSnapshot {
  id: string | null;
  name: string | null;
  available: boolean;
}

/** 生成结果条目内联摘要的最大字符数；批次列表与生成弹窗共用同一截断规则 */
export const GENERATION_EXCERPT_MAX_CHARS = 120;

/**
 * 把正文裁成列表内联摘要。超长时截断并加省略号。
 * 供 worker 投影与前端弹窗路径共用，保证两条生成路径的摘要形态一致。
 *
 * `hasMore` 供只拿到正文前缀的调用方（worker 用 SQL substr 取前缀以压小轮询响应）
 * 声明"原文还有更多"，避免前缀被空白折叠后短于上限、误判为完整正文而漏掉省略号。
 */
export function buildGenerationExcerpt(
  body: string | null | undefined,
  options?: { hasMore?: boolean },
): string | null {
  if (!body) return null;
  const normalized = body.replace(/\s+/g, ' ').trim();
  if (!normalized) return null;
  const chars = [...normalized];
  if (chars.length > GENERATION_EXCERPT_MAX_CHARS) {
    return chars.slice(0, GENERATION_EXCERPT_MAX_CHARS).join('') + '…';
  }
  return options?.hasMore ? normalized + '…' : normalized;
}

export interface ContentGenerationBatchItem {
  generation_id: string;
  request_id: string;
  topic_index: number;
  variant_index: number;
  topic: string;
  brief: string | null;
  source_saved_topic_id: string | null;
  status: ContentGenerationBatchItemStatus;
  failure_code: DraftGenerationFailureCode | 'content_generation.enqueue_failed' | null;
  safety_code: string | null;
  draft_id: string | null;
  version_id: string | null;
  draft_available: boolean;
  /** 模型产出的标题；草稿不可用或尚未就绪时为 null */
  title: string | null;
  /** 正文摘要（截断至 GENERATION_EXCERPT_MAX_CHARS）；正文落 R2 或尚未就绪时为 null */
  excerpt: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContentGenerationBatch {
  id: string;
  platform: z.infer<typeof Platform>;
  mode: 'single' | 'batch';
  status: ContentGenerationBatchStatus;
  model: ContentGenerationResourceSnapshot;
  skill: ContentGenerationResourceSnapshot | null;
  reference_note_id: string | null;
  reference_available: boolean;
  reference_snapshot: {
    title: string | null;
    author: string | null;
    summary: string | null;
    source_url: string | null;
  } | null;
  variant_count: number;
  topic_count: number;
  summary: DraftGenerationSummary;
  items: ContentGenerationBatchItem[];
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface ContentGenerationBatchResponse {
  batch: ContentGenerationBatch;
}

export const ContentGenerationResourceSnapshotResponse = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  available: z.boolean(),
});

export const ContentGenerationBatchItemResponse = z.object({
  generation_id: z.string().uuid(),
  request_id: z.string().uuid(),
  topic_index: z.number().int().positive(),
  variant_index: z.number().int().positive(),
  topic: z.string(),
  brief: z.string().nullable(),
  source_saved_topic_id: z.string().nullable(),
  status: z.enum(['queued', 'running', 'ready', 'blocked', 'failed']),
  failure_code: z
    .enum([
      'content_generation.prompt_unavailable',
      'content_generation.model_unavailable',
      'content_generation.model_failed',
      'content_generation.invalid_output',
      'content_generation.persistence_failed',
      'content_generation.internal_error',
      'content_generation.enqueue_failed',
    ])
    .nullable(),
  safety_code: z.string().nullable(),
  draft_id: z.string().nullable(),
  version_id: z.string().nullable(),
  draft_available: z.boolean(),
  // 新增字段必须可缺省：Pages 与 Workers 独立部署且必然漂移，
  // 前端先于 worker 上线时若设为必填，整个批次响应会在 api-client 的 safeParse 处失败。
  title: z.string().nullable().default(null),
  excerpt: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
});

export const ContentGenerationBatchResponseBody = z.object({
  batch: z.object({
    id: z.string().uuid(),
    platform: Platform,
    mode: z.enum(['single', 'batch']),
    status: z.enum(['queued', 'running', 'succeeded', 'partially_succeeded', 'failed']),
    model: ContentGenerationResourceSnapshotResponse,
    skill: ContentGenerationResourceSnapshotResponse.nullable(),
    reference_note_id: z.string().nullable(),
    reference_available: z.boolean(),
    reference_snapshot: z
      .object({
        title: z.string().nullable(),
        author: z.string().nullable(),
        summary: z.string().nullable(),
        source_url: z.string().nullable(),
      })
      .nullable(),
    variant_count: z.number().int().min(1).max(3),
    topic_count: z.number().int().min(1).max(5),
    summary: z.object({
      total: z.number().int().nonnegative(),
      ready: z.number().int().nonnegative(),
      blocked: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
    }),
    items: z.array(ContentGenerationBatchItemResponse),
    created_at: z.string(),
    updated_at: z.string(),
    completed_at: z.string().nullable(),
  }),
});

export const LatestContentGenerationBatchResponseBody = z.object({
  batch: ContentGenerationBatchResponseBody.shape.batch.nullable(),
});

export const ContentDraftsListQuery = z.object({
  platform: Platform.optional(),
  q: z.string().trim().min(1).max(120).optional(),
  status: z.enum(['draft', 'ready', 'published', 'archived']).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type ContentDraftsListQuery = z.infer<typeof ContentDraftsListQuery>;

export type ContentDraftStatus = 'draft' | 'ready' | 'published' | 'archived';
export type ContentDraftSourceType =
  | 'blank'
  | 'from_note'
  | 'from_topic'
  | 'from_template'
  | 'from_aigc';

export interface ContentDraftSummary {
  id: string;
  version_id: string | null;
  platform: z.infer<typeof Platform>;
  title: string | null;
  status: ContentDraftStatus;
  source_type: ContentDraftSourceType;
  topic: string | null;
  source_saved_topic_id: string | null;
  skill_id: string | null;
  safety_status: DraftSafetyStatus | null;
  pinned_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContentDraftsListResponse {
  items: ContentDraftSummary[];
  next_cursor: string | null;
}

export const SavedTopicsListQuery = z.object({
  platform: Platform.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type SavedTopicsListQuery = z.infer<typeof SavedTopicsListQuery>;

export const SavedTopicCreateRequest = z
  .object({
    platform: Platform,
    title: z.string().trim().min(1).max(120).optional(),
    topic: z.string().trim().min(1).max(200),
    brief: z.string().trim().max(2000).optional(),
    source_note_id: z.string().trim().min(1).max(128).optional(),
    source_opportunity_index: z.coerce.number().int().min(0).max(1000).optional(),
    source_competitor_refs: z.array(TopicCompetitorReferenceModelOutput).max(8).optional(),
    skill_hints: z.array(TopicSkillHintModelOutput).max(5).optional(),
    priority_score: z.coerce.number().int().min(0).max(100).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.source_opportunity_index === undefined || value.source_note_id) return;
    ctx.addIssue({
      code: 'custom',
      path: ['source_opportunity_index'],
      message: 'source_opportunity_index requires source_note_id',
    });
  });
export type SavedTopicCreateRequest = z.infer<typeof SavedTopicCreateRequest>;

export interface SavedTopicSummary {
  id: string;
  platform: z.infer<typeof Platform>;
  title: string | null;
  topic: string;
  brief: string | null;
  source_note_id: string | null;
  source_opportunity_index: number | null;
  source_competitor_refs: TopicCompetitorReference[];
  skill_hints: TopicSkillHint[];
  priority_score: number | null;
  created_at: string;
  updated_at: string;
}

export interface SavedTopicsListResponse {
  items: SavedTopicSummary[];
  next_cursor: string | null;
}

export interface SavedTopicCreateResponse {
  item: SavedTopicSummary;
}

export interface SavedTopicDeleteResponse {
  id: string;
  status: 'deleted';
}
export const ContentDraftUpdateRequest = z.object({
  title: z.string().trim().min(1).max(120),
  body_md: z.string().trim().min(1).max(30000),
});
export type ContentDraftUpdateRequest = z.infer<typeof ContentDraftUpdateRequest>;

export const RewriteContentDraftRequest = z.object({
  base_version_id: z.string().trim().min(1).max(128),
  model_id: z.string().trim().min(1).max(128),
  revision_instruction: z.string().trim().min(1).max(2000),
});
export type RewriteContentDraftRequest = z.infer<typeof RewriteContentDraftRequest>;

export type ContentDraftGenerationTaskType = 'aigc_generation' | 'aigc_rewrite';

export interface ContentDraftVersionSummary {
  id: string;
  version_number: number;
  title: string | null;
  body_size_bytes: number | null;
  ai_generated: boolean;
  source_aigc_record_id: string | null;
  source_prompt_version_id: string | null;
  generation_model_id: string | null;
  generation_model_name: string | null;
  generation_task_type: ContentDraftGenerationTaskType | null;
  base_version_id: string | null;
  base_version_number: number | null;
  diff_summary: string | null;
  created_by_user_id: string | null;
  created_at: string;
}

export interface ContentDraftVersion extends ContentDraftVersionSummary {
  body_md: string;
  revision_instruction: string | null;
}

export interface ContentDraftDetail extends ContentDraftSummary {
  current_version_id: string | null;
  current_version: ContentDraftVersion | null;
  versions: ContentDraftVersionSummary[];
  generation_batch?: {
    batch_id: string;
    status: ContentGenerationBatchStatus;
    platform: z.infer<typeof Platform>;
    topic_index: number;
    variant_index: number;
  } | null;
}

export interface ContentDraftReadResponse {
  draft: ContentDraftDetail;
}

export interface ContentDraftGenerationBatchReadResponse {
  generation_batch: ContentDraftDetail['generation_batch'];
}

export const ContentDraftGenerationBatchReadResponseBody = z.object({
  generation_batch: z
    .object({
      batch_id: z.string().uuid(),
      status: z.enum(['queued', 'running', 'succeeded', 'partially_succeeded', 'failed']),
      platform: Platform,
      topic_index: z.number().int().positive(),
      variant_index: z.number().int().positive(),
    })
    .nullable(),
});

export interface ContentDraftVersionReadResponse {
  version: ContentDraftVersion;
}

export interface ContentDraftUpdateResponse {
  draft: ContentDraftDetail;
}

export interface RewriteContentDraftResponse {
  draft: ContentDraftDetail;
  version_id: string;
}

export interface ContentDraftArchiveResponse {
  ok: true;
  draft_id: string;
  status: 'archived';
  archived_at: string;
  /** @deprecated 旧客户端兼容字段；其值等于 archived_at，不表示逻辑删除。 */
  deleted_at: string;
}

export interface ContentDraftDeleteResponse {
  ok: true;
  draft_id: string;
  deleted_at: string;
}

export const ContentDraftPinRequest = z.object({
  pinned: z.boolean(),
});
export type ContentDraftPinRequest = z.infer<typeof ContentDraftPinRequest>;

export interface ContentDraftPinResponse {
  draft_id: string;
  pinned_at: string | null;
}

/**
 * Model output contract for T2 single-draft generation.
 * The API validates this shape server-side and never returns the prompt body or skill internals.
 */
export const GeneratedDraftModelOutput = z.object({
  title: z.string().trim().min(1).max(120),
  body_md: z.string().trim().min(1).max(30000),
});
export type GeneratedDraftModelOutput = z.infer<typeof GeneratedDraftModelOutput>;
